import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import { Card, Input, Select, SearchableSelect, Button, Badge, Spinner, ProductImage, cx } from "../components/ui";
import { fmtEur, fmtNum } from "../lib/format";

type Exclusion = { system: "ExactOnline" | "Lightspeed"; reason: "category" | "sku" | "keyword" };
const REASON_LABEL: Record<Exclusion["reason"], string> = { category: "category", sku: "SKU", keyword: "keyword" };

/** Short badge text: "excluded" or "excl. ExactOnline" when only one destination is excluded. */
function exclusionLabel(ex: Exclusion[] | undefined): string {
  if (!ex || ex.length === 0) return "excluded";
  if (ex.length === 1 && ex[0]) return `excl. ${ex[0].system === "ExactOnline" ? "ExactOnline" : "LS"}`;
  return "excluded";
}
/** Full tooltip, e.g. "Excluded from ExactOnline (category) + Lightspeed (SKU)". */
function exclusionTitle(ex: Exclusion[] | undefined): string {
  if (!ex || ex.length === 0) return "Excluded";
  return "Excluded from " + ex.map((e) => `${e.system} (${REASON_LABEL[e.reason]})`).join(" + ");
}

/** Deposit (statiegeld) / levy (Stibat, Thuiskopie) badges — amounts for the pack actually sold (× pick size).
 *  Both are included in the posted purchase/sales prices; the deposit also gets a notice in the shop text. */
function CostBadges({ deposit, levy, pickSize }: { deposit: number; levy: number; pickSize: number | null }) {
  const pick = pickSize || 1;
  if (deposit <= 0 && levy <= 0) return null;
  return (
    <>
      {deposit > 0 && (
        <span title={`Deposit (statiegeld) € ${(deposit * pick).toFixed(2)} per pack — included in the posted prices`}>
          <Badge color="amber">♻ € {(deposit * pick).toFixed(2)}</Badge>
        </span>
      )}
      {levy > 0 && (
        <span title={`Levy (Stibat / Thuiskopie) € ${(levy * pick).toFixed(2)} per pack — included in the posted prices`}>
          <Badge color="amber">⚡ € {(levy * pick).toFixed(2)}</Badge>
        </span>
      )}
    </>
  );
}

/** Where this PBS product currently lives downstream: ExactOnline (master item) and Lightspeed (webshop). */
function Presence({ inExact, inLs, itemCode }: { inExact: boolean; inLs: boolean; itemCode?: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span title={inExact ? `In ExactOnline${itemCode ? ` as ${itemCode}` : ""}` : "Not yet in ExactOnline"}><Badge color={inExact ? "green" : "gray"}>{inExact ? "ExactOnline ✓" : "ExactOnline —"}</Badge></span>
      <span title={inLs ? "On the Lightspeed webshop" : "Not on Lightspeed"}><Badge color={inLs ? "blue" : "gray"}>{inLs ? "LS ✓" : "LS —"}</Badge></span>
    </div>
  );
}

export default function Products() {
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [active, setActive] = useState<"all" | "active" | "inactive">("all");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [pickSize, setPickSize] = useState("");
  const [inExact, setInExact] = useState<"all" | "yes" | "no">("all");
  const [inLs, setInLs] = useState<"all" | "yes" | "no">("all");
  const [excluded, setExcluded] = useState<"all" | "exact" | "lightspeed">("all");
  const [costs, setCosts] = useState<"all" | "deposit" | "levy">("all");
  // Category filter, seeded from the URL (?category=…&catname=…) — e.g. clicking a product count on
  // the Mapping page opens this list scoped to that PBS category.
  const [catFilter, setCatFilter] = useState<{ id: string; name: string } | null>(() => {
    const p = new URLSearchParams(window.location.search);
    const id = p.get("category");
    return id ? { id, name: p.get("catname") || id } : null;
  });
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"grid" | "table">("table");

  const reset = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };
  const clearCatFilter = () => { setCatFilter(null); setPage(1); window.history.replaceState({}, "", "/products"); };

  const brands = trpc.pbs.brands.useQuery();
  const q = trpc.pbs.search.useQuery({ search: search || undefined, brand: brand || undefined, active, inStockOnly, pickSize: pickSize ? Number(pickSize) : undefined, inExact, inLightspeed: inLs, excluded, costs, categoryId: catFilter?.id, page, pageSize: 24 });
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.pageSize)) : 1;
  const elig = trpc.pbs.catalogEligibility.useQuery(undefined, { staleTime: 60_000 });

  // Export downloads the CURRENT filtered list (all matching rows, not just this page) with every
  // column we hold — same filter params as the search query above, handled by /api/export/products.
  const exportUrl = (format: "xlsx" | "csv") => {
    const p = new URLSearchParams();
    if (search.trim()) p.set("search", search.trim());
    if (brand) p.set("brand", brand);
    if (active !== "all") p.set("active", active);
    if (inStockOnly) p.set("inStockOnly", "true");
    if (pickSize) p.set("pickSize", pickSize);
    if (inExact !== "all") p.set("inExact", inExact);
    if (inLs !== "all") p.set("inLightspeed", inLs);
    if (excluded !== "all") p.set("excluded", excluded);
    if (costs !== "all") p.set("costs", costs);
    if (catFilter) p.set("categoryId", catFilter.id);
    p.set("format", format);
    return `/api/export/products?${p.toString()}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Products</h1>
          <p className="text-sm text-slate-500">{fmtNum(q.data?.total)} products in the PBS catalog</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => { window.location.href = exportUrl("xlsx"); }}
            title={`Download the current filtered list (${fmtNum(q.data?.total)} products) as Excel, with all product data`}
          >
            ⬇ Export Excel{q.data ? ` (${fmtNum(q.data.total)})` : ""}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { window.location.href = exportUrl("csv"); }}
            title="Same export as CSV"
          >
            CSV
          </Button>
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
            <button onClick={() => setView("grid")} className={cx("rounded-md px-3 py-1 text-sm", view === "grid" ? "bg-indigo-600 text-white" : "text-slate-600")}>Grid</button>
            <button onClick={() => setView("table")} className={cx("rounded-md px-3 py-1 text-sm", view === "table" ? "bg-indigo-600 text-white" : "text-slate-600")}>Table</button>
          </div>
        </div>
      </div>

      {/* Catalog funnel: how many PBS items are eligible (not excluded) vs excluded per destination. */}
      {elig.data && (
        <div>
          <div className="grid gap-3 sm:grid-cols-2">
            {([
              { key: "exact" as const, title: "PBS → ExactOnline", d: elig.data.exact, extra: null as null | { mapped: number } },
              { key: "lightspeed" as const, title: "PBS → Lightspeed", d: elig.data.lightspeed, extra: { mapped: elig.data.lightspeed.mapped } },
            ]).map(({ key, title, d, extra }) => (
              <div key={key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">{title}</span>
                  <span className="text-xs text-slate-400">{fmtNum(elig.data!.total)} in PBS catalog</span>
                </div>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                  <div><div className="text-2xl font-semibold tabular-nums text-emerald-600">{fmtNum(d.eligible)}</div><div className="text-xs text-slate-500">eligible (not excluded)</div></div>
                  {extra && <div><div className="text-2xl font-semibold tabular-nums text-indigo-600">{fmtNum(extra.mapped)}</div><div className="text-xs text-slate-500">mapped → publishable</div></div>}
                  <div><div className="text-2xl font-semibold tabular-nums text-slate-400">{fmtNum(d.excluded)}</div><div className="text-xs text-slate-500">excluded</div></div>
                </div>
                {d.excluded > 0 && (
                  <div className="mt-2 text-[11px] text-slate-400">excluded by: {fmtNum(d.byCategory)} category · {fmtNum(d.bySku)} SKU · {fmtNum(d.byKeyword)} keyword</div>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            "Eligible" = not excluded by your category / SKU / name-keyword filters (Settings → Exclusions). Lightspeed also needs a{" "}
            <span className="font-medium">mapped category</span> (and stock) to actually publish — "mapped → publishable" is the count that clears the mapping gate.
          </p>
        </div>
      )}

      <Card pad>
        {catFilter && (
          <div className="mb-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700 ring-1 ring-inset ring-indigo-200">
              Category: <span className="font-medium">{catFilter.name}</span> <span className="font-mono text-[11px] text-indigo-400">{catFilter.id}</span>
              <button onClick={clearCatFilter} className="leading-none text-indigo-400 transition hover:text-red-600" title="Clear category filter">×</button>
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search code, description, brand, EAN…" value={search} onChange={(e) => reset(setSearch)(e.target.value)} className="w-80" />
          <SearchableSelect
            value={brand}
            onChange={reset(setBrand)}
            options={[{ value: "", label: "All brands" }, ...(brands.data ?? []).map((b) => ({ value: b, label: b }))]}
            placeholder="All brands"
            className="w-56"
          />
          <Select value={active} onChange={(e) => reset(setActive)(e.target.value as typeof active)}>
            <option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </Select>
          <Input type="number" min="1" placeholder="pick size" value={pickSize} onChange={(e) => reset(setPickSize)(e.target.value)} className="w-28" title="Filter by PBS pick size" />
          <Select value={inExact} onChange={(e) => reset(setInExact)(e.target.value as typeof inExact)} title="Whether the item exists in ExactOnline">
            <option value="all">ExactOnline: any</option><option value="yes">In ExactOnline</option><option value="no">Not in ExactOnline</option>
          </Select>
          <Select value={inLs} onChange={(e) => reset(setInLs)(e.target.value as typeof inLs)} title="Whether the item exists on Lightspeed">
            <option value="all">LS: any</option><option value="yes">On Lightspeed</option><option value="no">Not on LS</option>
          </Select>
          <Select value={excluded} onChange={(e) => reset(setExcluded)(e.target.value as typeof excluded)} title="Show only products excluded for a destination (by category / SKU / keyword)">
            <option value="all">Excluded: any</option><option value="exact">Excluded from ExactOnline</option><option value="lightspeed">Excluded from LS</option>
          </Select>
          <Select value={costs} onChange={(e) => reset(setCosts)(e.target.value as typeof costs)} title="Show only products carrying a deposit (statiegeld) or a levy (Stibat battery contribution / Thuiskopie)">
            <option value="all">Deposit/levy: any</option><option value="deposit">♻ With deposit</option><option value="levy">⚡ With levy</option>
          </Select>
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={inStockOnly} onChange={(e) => reset(setInStockOnly)(e.target.checked)} className="accent-indigo-600" /> In stock
          </label>
        </div>
      </Card>

      {q.isLoading ? (
        <Card><Spinner label="Loading products…" /></Card>
      ) : !q.data?.rows.length ? (
        <Card><p className="text-sm text-slate-500">No products match these filters.</p></Card>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {q.data.rows.map((p) => (
            <Link key={p.id} href={`/products/${p.productCode}`} className="group rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md hover:border-indigo-200">
              <div className="relative aspect-square overflow-hidden rounded-t-2xl">
                <ProductImage file={p.productImage} alt={p.descriptionNL ?? p.productCode} className="h-full w-full p-3 transition group-hover:scale-105" />
                <div className="absolute left-2 top-2 flex flex-col items-start gap-1">
                  {p.active ? <Badge color="green">active</Badge> : <Badge color="gray">inactive</Badge>}
                  {(p.exclusions?.length ?? 0) > 0 && <span title={exclusionTitle(p.exclusions)}><Badge color="red">{exclusionLabel(p.exclusions)}</Badge></span>}
                  <CostBadges deposit={p.depositTotal} levy={p.levyTotal} pickSize={p.pickSize} />
                </div>
              </div>
              <div className="space-y-1 border-t border-slate-100 p-3">
                <div className="font-mono text-xs text-slate-400">{p.productCode}</div>
                <div className="line-clamp-2 min-h-[2.5rem] text-sm font-medium text-slate-800">{p.descriptionNL || <span className="text-slate-300">No description</span>}</div>
                <div className="flex items-center justify-between pt-1 text-xs">
                  <span className="text-slate-500">{p.brand}</span>
                  <span className="text-slate-600"><span className="text-slate-400">buy</span> {fmtEur(p.netPrice)} · <span className="text-slate-400">sell</span> <span className="font-semibold text-slate-900">{fmtEur(p.retailPriceExclVat)}</span></span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <Presence inExact={p.inExact} inLs={p.inLightspeed} itemCode={p.exactItemCode} />
                  <span className="text-xs text-slate-400">stock {fmtNum(p.stockValue)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <Card pad={false}>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="p-3"></th><th className="p-3">Code</th><th>Description</th><th>Brand</th><th>Status</th><th>Synced</th><th>Deposit/levy</th><th className="text-right">Stock</th><th className="text-right">Net purchase</th><th className="p-3 text-right">Sales (excl)</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.rows.map((p) => (
                <tr key={p.id} className={cx("hover:bg-slate-50", (p.exclusions?.length ?? 0) > 0 && "bg-red-50/60")}>
                  <td className="p-2"><Link href={`/products/${p.productCode}`}><ProductImage file={p.productImage} className="h-12 w-12 rounded-lg border border-slate-100" /></Link></td>
                  <td className="p-3 font-mono text-xs"><Link href={`/products/${p.productCode}`} className="text-indigo-600">{p.productCode}</Link></td>
                  <td className="max-w-md truncate">{p.descriptionNL}</td>
                  <td>{p.brand}</td>
                  <td>{p.active ? <Badge color="green">active</Badge> : <Badge color="gray">inactive</Badge>}</td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      <Presence inExact={p.inExact} inLs={p.inLightspeed} itemCode={p.exactItemCode} />
                      {(p.exclusions?.length ?? 0) > 0 && <span title={exclusionTitle(p.exclusions)}><Badge color="red">{exclusionLabel(p.exclusions)}</Badge></span>}
                    </div>
                  </td>
                  <td><div className="flex flex-wrap items-center gap-1"><CostBadges deposit={p.depositTotal} levy={p.levyTotal} pickSize={p.pickSize} /></div></td>
                  <td className="text-right">{fmtNum(p.stockValue)}</td>
                  <td className="text-right text-slate-600">{fmtEur(p.netPrice)}</td>
                  <td className="p-3 text-right font-medium">{fmtEur(p.retailPriceExclVat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Previous</Button>
        <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
        <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</Button>
      </div>
    </div>
  );
}
